import { API_TYPES, post } from "@src/appV2/api";
import { useCbhQueryClient } from "@src/appV2/api/queryClient";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { PLACEMENT_PATHS } from "@src/appV2/redesign/Placements/paths";
import { showApplicationsStorageKey } from "@src/appV2/redesign/Placements/utils";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { z } from "zod";

import { FavoritePlacementSuccessMessage } from "../components/FavoritePlacementSuccessMessage";
import { workerFavoritePlacementDataSchema } from "../types/workerFavoritePlacement.schema";
import {
  cancelPlacementFetchingQueries,
  invalidatePlacementFetchingQueries,
  optimisticallySetPlacementFavoritedState,
  optimisticallySetPlacementWorkerFavoritePlacementId,
} from "./placementFavoriteCacheUtils";

// Local schemas - specific to this hook
const createWorkerFavoritePlacementRequestSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workerFavoritePlacement),
    relationships: z.object({
      placement: z.object({
        data: z.object({
          type: z.literal(API_TYPES.placement),
          id: z.string(),
        }),
      }),
    }),
  }),
});

const createWorkerFavoritePlacementResponseSchema = z.object({
  data: workerFavoritePlacementDataSchema,
});

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createWorkerFavoritePlacementParamsSchema = z.object({
  workerId: z.string(),
  placementId: z.string(),
  source: z.string(),
});

type CreateWorkerFavoritePlacementParams = z.infer<
  typeof createWorkerFavoritePlacementParamsSchema
>;

type CreateWorkerFavoritePlacementResponse = z.infer<
  typeof createWorkerFavoritePlacementResponseSchema
>;

export function useCreateWorkerFavoritePlacement(
  options: UseMutationOptions<
    CreateWorkerFavoritePlacementResponse,
    string,
    CreateWorkerFavoritePlacementParams
  > = {}
) {
  const queryClient = useCbhQueryClient();
  const { showSuccessToast } = useToast();
  const [, setShowApplications] = useLocalStorage<boolean>(showApplicationsStorageKey, true);
  const history = useHistory();

  return useMutation({
    mutationFn: async (params: CreateWorkerFavoritePlacementParams) => {
      const { workerId, placementId } = params;

      await cancelPlacementFetchingQueries(queryClient, workerId, placementId);

      optimisticallySetPlacementFavoritedState(queryClient, workerId, placementId, true);

      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/favorite-placements`,
        data: {
          data: {
            type: API_TYPES.workerFavoritePlacement,
            relationships: {
              placement: {
                data: {
                  type: API_TYPES.placement,
                  id: placementId,
                },
              },
            },
          },
        },
        requestSchema: createWorkerFavoritePlacementRequestSchema,
        responseSchema: createWorkerFavoritePlacementResponseSchema,
      });

      const workerFavoritePlacementId = response.data.data.id;
      optimisticallySetPlacementWorkerFavoritePlacementId(
        queryClient,
        workerId,
        placementId,
        workerFavoritePlacementId
      );

      return response.data;
    },
    onSuccess: async (_data, variables, _context) => {
      const { workerId, placementId, source } = variables ?? {};

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_FAVORITED, {
        placementId,
        workerId,
        source,
      });

      await invalidatePlacementFetchingQueries(queryClient, workerId, placementId);

      // Show success toast with link
      showSuccessToast(
        <FavoritePlacementSuccessMessage
          onClick={() => {
            setShowApplications(true);

            history.push(PLACEMENT_PATHS.ROOT);
          }}
        />,
        {
          anchorOrigin: {
            horizontal: "center",
            vertical: "bottom",
          },
          style: {
            marginBottom: "calc(env(safe-area-inset-bottom) + 120px)",
          },
        }
      );
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PLACEMENT_FAVORITED_FAILURE,
    },
    ...options,
  });
}
